import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import ProfileSetup from '@/pages/ProfileSetup';
import Deals from '@/pages/Deals';
import Store from '@/pages/Store';
import MyStore from '@/pages/MyStore';
import FunTime from '@/pages/FunTime';
import NetworkPage from '@/pages/Network';
import Admin from '@/pages/Admin';
import AdminLogin from '@/pages/AdminLogin';
import PublicProfile from '@/pages/PublicProfile';
import Storefront from '@/pages/Storefront';
import BrandStore from '@/pages/BrandStore';
import Notifications from '@/pages/Notifications';
import PasswordLogin from '@/pages/PasswordLogin';
import ResetPassword from '@/pages/ResetPassword';
import PayLink from '@/pages/PayLink';
import RankTeam from '@/pages/RankTeam';
import Business from '@/pages/Business';

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="loading-state">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Force profile completion: name, phone, pincode & town are mandatory before using INDIVALIFE.
  // Phone is required because Razorpay's checkout cannot pre-fill it otherwise — the
  // user gets stuck on Razorpay's "Enter mobile number" step which has known
  // tap/focus bugs on some Android/Chrome combos.
  const phoneDigits = String(user.phone || '').replace(/\D/g, '');
  const incomplete = !user.pincode || !user.town || !user.name || phoneDigits.length < 10;
  if (incomplete && !location.pathname.startsWith('/profile') && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/profile" replace />;
  }
  return children;
};

// The company store is PUBLIC — customers arriving via a distributor's referral
// store link can browse & buy without signing in. Logged-in members still get the
// full app chrome (nav) around it.
const StoreGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  return user ? <Layout><Store /></Layout> : <Store />;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<PasswordLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pay/:token" element={<PayLink />} />
      <Route path="/p/:personalId" element={<PublicProfile />} />
      <Route path="/s/:slug" element={<Storefront />} />
      <Route path="/store/:personalId" element={<BrandStore />} />
      <Route path="/store" element={<StoreGate />} />
      <Route path="/store/p/:productId" element={<StoreGate />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/:secret" element={<Protected><Admin /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/profile" element={<ProfileSetup />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/my-store" element={<MyStore />} />
        <Route path="/rank" element={<RankTeam />} />
        <Route path="/business" element={<Business />} />
        <Route path="/fun-time" element={<FunTime />} />
        <Route path="/network" element={<NetworkPage />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>
      {/* URL aliases for common typos / older shortcuts */}
      <Route path="/toolbox/:tab" element={<Navigate to="/store" replace />} />
      <Route path="/toolbox" element={<Navigate to="/store" replace />} />
      <Route path="/chat" element={<Navigate to="/store" replace />} />
      <Route path="/chats" element={<Navigate to="/store" replace />} />
      <Route path="/funtime" element={<Navigate to="/fun-time" replace />} />
      <Route path="/fun" element={<Navigate to="/fun-time" replace />} />
      <Route path="/tambola" element={<Navigate to="/fun-time" replace />} />
      <Route path="/deal" element={<Navigate to="/deals" replace />} />
      <Route path="/home" element={<Navigate to="/store" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
